import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ISort, SortType } from '@entities/util/ISort';
import { sortHandler } from '@app/utils/sort';

type UrlParams = { [key: string]: string } | null;

export const useSort = (urlParams: UrlParams) => {
  const [value, updateValue] = useState<ISort | null>(null);
  const SORT = 'sort';

  const init = () => {
    if (!urlParams) {
      return;
    }

    if (urlParams[SORT]) {
      updateValue(JSON.parse(urlParams[SORT]));
    } else {
      updateValue(null);
    }
  };

  const setValue = (name?: string | null) => {
    if (!name) {
      updateValue(null);

      return;
    }

    updateValue(sortHandler(value, name));
  };

  const formatForUrl = () => {
    return {
      [SORT]: value ? JSON.stringify(value) : '',
    };
  };

  const formatForRequest = () => {
    return {
      [SORT]: value,
    };
  };

  useEffect(init, [urlParams]);

  return { setValue, formatForRequest, formatForUrl, value };
};

export const useSearch = (urlParams: UrlParams) => {
  const [value, setValue] = useState<string>('');
  const SEARCH = 'search';

  const init = () => {
    if (!urlParams) {
      return;
    }

    setValue(urlParams[SEARCH] ? urlParams[SEARCH] : '');
  };

  const formatForUrl = () => {
    return value
      ? {
          [SEARCH]: value ? value : null,
        }
      : {};
  };

  const formatForRequest = () => {
    return {
      [SEARCH]: value,
    };
  };

  useEffect(init, [urlParams]);

  return { setValue, formatForRequest, formatForUrl, value };
};

export const usePage = (urlParams: UrlParams, defaultPage: number) => {
  const [value, setValue] = useState<number | null>(null);
  const PAGE = 'page';

  const init = () => {
    if (!urlParams) {
      return;
    }

    setValue(urlParams[PAGE] ? +urlParams[PAGE] : defaultPage);
  };

  const formatForUrl = () => {
    return {
      [PAGE]: value ? `${value}` : null,
    };
  };

  const formatForRequest = () => {
    return {
      [PAGE]: value ? `${value}` : null,
    };
  };

  useEffect(init, [urlParams]);

  return { formatForUrl, formatForRequest, setValue, value };
};

export const useTab = (urlParams: UrlParams, defaultPage: number) => {
  const [value, setValue] = useState<number | null>(null);
  const TAB = 'tab';

  const init = () => {
    if (!urlParams) {
      return;
    }

    setValue(urlParams[TAB] ? +urlParams[TAB] : defaultPage);
  };

  const formatForUrl = () => {
    return {
      [TAB]: value ? `${value}` : null,
    };
  };

  const formatForRequest = () => {
    return {
      [TAB]: value ? `${value}` : null,
    };
  };

  useEffect(init, [urlParams]);

  return { formatForUrl, formatForRequest, setValue, value };
};

export const useLocation = (defaultLocation: string) => {
  const LOCATION = 'location';

  const formatForRequest = () => {
    return {
      [LOCATION]: defaultLocation,
    };
  };

  const formatForUrl = () => {
    return {
      [LOCATION]: defaultLocation,
    };
  };

  return { formatForRequest, formatForUrl, value: LOCATION };
};

export const useLimit = (urlParams: UrlParams, defaultItemsPerPage: number) => {
  const [value, setValue] = useState<number | null>(null);
  const ITEMS_PER_PAGE = 'limit';

  const init = () => {
    if (!urlParams) {
      return;
    }

    setValue(urlParams[ITEMS_PER_PAGE] ? +urlParams[ITEMS_PER_PAGE] : defaultItemsPerPage);
  };

  const formatForUrl = () => {
    return {
      [ITEMS_PER_PAGE]: value ? `${value}` : null,
    };
  };

  const formatForRequest = () => {
    return {
      [ITEMS_PER_PAGE]: value ? `${value}` : null,
    };
  };

  useEffect(init, [urlParams]);

  return { setValue, formatForRequest, formatForUrl, value };
};

export const useFilters = (urlParams: UrlParams, defaultFilters: any) => {
  const [value, setValue] = useState<any>(null);
  const FILTERS = 'filters';

  const defaultValues = useMemo(() => defaultFilters, []);

  const init = () => {
    if (!urlParams) {
      return;
    }

    if (urlParams[FILTERS]) {
      const params = JSON.parse(urlParams[FILTERS]);
      setValue(params);
    } else {
      setValue(defaultValues);
    }
  };

  const formatForUrl = () => {
    return {
      [FILTERS]: value ? JSON.stringify(value) : null,
    };
  };

  const formatForRequest = () => {
    return {
      [FILTERS]: value ? value : null,
    };
  };

  useEffect(init, [urlParams]);

  return { setValue, formatForRequest, formatForUrl, value };
};

// export const useDateRange = (urlParams: UrlParams, defaultStartDate?: Date, defaultEndDate?: Date) => {
//   const [value, updateDateRange] = useState<DateRangeType | null>(null);
//   const DATE_RANGE = 'dateRange';
//
//   const init = () => {
//     if (!urlParams) {
//       return;
//     }
//
//     if (urlParams[DATE_RANGE]) {
//       const params = JSON.parse(urlParams[DATE_RANGE]);
//
//       setValue({ startDate: new Date(params.startDate), endDate: new Date(params.endDate) });
//     } else {
//       setValue({ startDate: defaultStartDate, endDate: defaultEndDate });
//     }
//   };
//
//   const formatForRequest = () => {
//     return {
//       [DATE_RANGE]: value ? convertDateRangeForServer(value) : null,
//     };
//   };
//
//   const formatForUrl = () => {
//     return {
//       [DATE_RANGE]: value ? JSON.stringify(convertDateRangeForServer(value)) : null,
//     };
//   };
//
//   const setValue = (data: DateRangeType) => {
//     updateDateRange(data);
//   };
//
//   useEffect(init, [urlParams]);
//
//   return { setValue, formatForRequest, formatForUrl, value };
// };

export const useAdditionalParams = (urlParams: UrlParams, values: any) => {
  const [value, setValue] = useState<any>(null);

  const init = () => {
    if (!urlParams) {
      return;
    }

    setValue(values);
  };

  const formatForUrl = () => {
    return {};
  };

  const formatForRequest = () => {
    return values;
  };

  useEffect(init, [urlParams]);

  return { setValue, formatForRequest, formatForUrl, value };
};

export const useListParamsAndPersistToUrl = (
  ...rest: {
    formatForRequest: () => { [key: string]: any };
    formatForUrl: () => { [key: string]: any };
    value: any;
  }[]
) => {
  const [, updateSearchParams] = useSearchParams();

  useEffect(
    () => {
      const values = rest.map(item => item.value);

      // if (isObjectHasNullValues(values)) {
      //   return null;
      // }

      const searchParams = rest.reduce(
        (prev: any, cur: any) => ({ ...prev, ...cur.formatForUrl() }),
        {}
      );
      updateSearchParams(searchParams, { replace: true });
    },
    rest.map(item => item.value)
  );

  return useMemo(
    () => {
      const values = rest.map(item => item.value);

      // if (isObjectHasNullValues(values)) {
      //   return null;
      // }

      return rest.reduce((prev: any, cur: any) => ({ ...prev, ...cur.formatForRequest() }), {});
    },
    rest.map(item => item.value)
  );
};

export const useListParams = (
  ...rest: {
    formatForRequest: () => { [key: string]: any };
    formatForUrl: () => { [key: string]: any };
    value: any;
  }[]
) => {
  return useMemo(
    () => {
      const values = rest.map(item => item.value);

      if (isObjectHasNullValues(values)) {
        return null;
      }

      return rest.reduce((prev: any, cur: any) => ({ ...prev, ...cur.formatForRequest() }), {});
    },
    rest.map(item => item.value)
  );
};

export const useActualizePage = (
  pagination: {
    setValue: (value: number) => void;
    value: number;
  },
  totalPages: number
) => {
  useEffect(() => {
    if (!pagination.value || !totalPages) {
      return;
    }

    if (pagination.value > totalPages) {
      pagination.setValue(totalPages);
    }
  }, [pagination.value, totalPages]);
};
